/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import fileDownload from 'js-file-download';
import { PayloadAction } from '@reduxjs/toolkit';

import {
	getAttachmentTypesRequest,
	getAttachmentTypesSuccess,
	getEmAttachmentsRequest,
	getEmAttachmentsSuccess,
	uploadEmAttachmentRequest,
	uploadEmAttachmentSuccess,
	downloadEmAttachmentRequest,
	downloadEmAttachmentSuccess,
	deleteEmAttachmentRequest,
	deleteEmAttachmentSuccess,
	downloadAttachmentByAttachmentIdRequest,
	downloadAttachmentByAttachmentIdSuccess,
	generateUploadLinkRequest,
	generateUploadLinkSuccess,
	resetGenerateUploadLink,
} from './reducer';
import {
	TAttachmentTypesResponsePayload,
	TEmAttachmentsListRequest,
	TEmAttachmentsListResponsePayload,
	TEmAttachmentUploadRequest,
	TEmAttachmentDownloadRequest,
	TEmAttachmentDeleteRequest,
	TEmAttachmentDownloadByAttachmentIdRequest,
	TGenerateUploadLinkRequest,
	TGenerateUploadLinkResponse,
} from './types';

function* getAttachmentTypesRequestWorker(): Generator<any, void, any> {
	try {
		const response: TAttachmentTypesResponsePayload = yield call(api.emAttachments.getTypes);
		yield put(getAttachmentTypesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* getEmAttachmentsRequestWorker(
	action: PayloadAction<TEmAttachmentsListRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const response: TEmAttachmentsListResponsePayload = yield call(
			api.attachments.getList,
			payload,
		);
		yield put(getEmAttachmentsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(getEmAttachmentsSuccess(null));
	}
}

function* uploadEmAttachmentRequestWorker(
	action: PayloadAction<TEmAttachmentUploadRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const formData = new FormData();
		formData.append('file', payload.file);
		formData.append('typeId', String(payload.typeId));
		if (payload.comment) formData.append('comment', payload.comment);

		yield call(api.emAttachments.upload, { emId: payload.emId, formData });
		yield put(uploadEmAttachmentSuccess());

		if (payload.listRequest) {
			yield put(getEmAttachmentsRequest(payload.listRequest));
		}

		notificationContainer('Attachment uploaded successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(uploadEmAttachmentSuccess());
	}
}

function* downloadEmAttachmentRequestWorker(
	action: PayloadAction<TEmAttachmentDownloadRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const blob = yield call(api.attachments.getOneByEmId, payload);
		yield put(downloadEmAttachmentSuccess());
		if (blob) fileDownload(blob, payload.fileName);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(downloadEmAttachmentSuccess());
	}
}

function* downloadAttachmentByAttachmentIdRequestWorker(
	action: PayloadAction<TEmAttachmentDownloadByAttachmentIdRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const blob = yield call(api.attachments.downloadByAttachmentId, payload);
		yield put(downloadAttachmentByAttachmentIdSuccess());
		if (blob) fileDownload(blob, payload.fileName);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(downloadAttachmentByAttachmentIdSuccess());
	}
}

function* deleteEmAttachmentRequestWorker(
	action: PayloadAction<TEmAttachmentDeleteRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		yield call(api.emAttachments.delete, payload);
		yield put(deleteEmAttachmentSuccess());
		if (payload.listRequest) {
			yield put(getEmAttachmentsRequest(payload.listRequest));
		}
		notificationContainer('Attachment deleted', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(deleteEmAttachmentSuccess());
	}
}

function* generateUploadLinkRequestWorker(
	action: PayloadAction<TGenerateUploadLinkRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const response: TGenerateUploadLinkResponse = yield call(
			api.emAttachments.generateUploadLink,
			payload,
		);
		yield put(generateUploadLinkSuccess(response));
		notificationContainer('Upload link generated successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(generateUploadLinkSuccess(null));
	}
}

export function* attachmentsSaga(): Generator<any, void, any> {
	yield takeEvery(getAttachmentTypesRequest.type, getAttachmentTypesRequestWorker);
	yield takeEvery(getEmAttachmentsRequest.type, getEmAttachmentsRequestWorker);
	yield takeEvery(uploadEmAttachmentRequest.type, uploadEmAttachmentRequestWorker);
	yield takeEvery(downloadEmAttachmentRequest.type, downloadEmAttachmentRequestWorker);
	yield takeEvery(
		downloadAttachmentByAttachmentIdRequest.type,
		downloadAttachmentByAttachmentIdRequestWorker,
	);
	yield takeEvery(deleteEmAttachmentRequest.type, deleteEmAttachmentRequestWorker);
	yield takeEvery(generateUploadLinkRequest.type, generateUploadLinkRequestWorker);
}
